import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { App as AntdApp, ConfigProvider } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';

import { auth0Config, getAuth0ConfigError, isAuth0Configured } from './config/auth0';
import { setAuthTokenProvider, setSessionExpiredHandler } from './config/api';
import { identifyOperator, resetAnalytics } from './lib/analytics';
import { resetCurrentUserCache, useCurrentUser } from './hooks/useCurrentUser';
import { antdTheme } from './theme/antd-theme';
import ErrorBoundary from './components/Common/ErrorBoundary';
import LoadingSpinner from './components/Common/LoadingSpinner';
import TriageLayout from './components/Layout/TriageLayout';
import { TriageProvider } from './contexts/TriageContext';
import { useTriageParams } from './hooks/useTriageParams';
import TriagePage from './pages/TriagePage';
import WorkTasksPage from './pages/WorkTasksPage';
import ActionsPage from './pages/ActionsPage';
import TriageAgentHealth from './pages/TriageAgentHealth';
import UsagePage from './pages/UsagePage';
import HelpPage from './pages/HelpPage';
import WorklistPage from './pages/WorklistPage';

const TriageWithContext: React.FC<{ mode: 'triage' | 'issues' }> = ({ mode }) => {
  const { setParam } = useTriageParams();
  // Clearing `issue` here (not just setting `loc`) keeps the URL honest: an
  // `issue` param from an earlier deep link otherwise lingers indefinitely
  // across manual location switches, which used to fool TriagePage's
  // deep-link-preservation check into thinking every later location change
  // was still "the" deep link (reported 2026-07-12; also gated on the
  // TriagePage side via isInitialLocationEvalRef, but clearing it here is
  // the more honest fix — the URL shouldn't claim a stale issue at all).
  const handleSelectLocation = useCallback((id: string | null) => {
    setParam('loc', id);
    setParam('issue', null);
  }, [setParam]);
  const handleSelectTicket = useCallback(
    (id: number | null) => setParam('ticket', id ? String(id) : null),
    [setParam],
  );

  return (
    <TriageProvider onSelectLocation={handleSelectLocation} onSelectTicket={handleSelectTicket}>
      <TriagePage mode={mode} />
    </TriageProvider>
  );
};

const AppWithProviders: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();

  // PostHog identify the moment Auth0 hands us a user — events captured
  // before this fire as anonymous and get stitched to the operator's
  // distinct_id on the next event. resetAnalytics() runs on logout so
  // the next operator on the same browser starts a clean session.
  useEffect(() => {
    if (isAuthenticated && user) {
      identifyOperator(user);
    }
  }, [isAuthenticated, user]);

  // Wire Auth0 token + session expiry handler into the REST API client
  useEffect(() => {
    const redirectToLogin = () => {
      loginWithRedirect({
        authorizationParams: { audience: auth0Config.audience, scope: auth0Config.scope },
        // Include search + hash so deep links like /issues?issue=123 survive
        // the Auth0 round-trip. Previously the query string was dropped and
        // the user landed on /issues with no selected issue.
        appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
      });
    };

    setAuthTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch (err: unknown) {
        const error = err as Error;
        const message = error?.message || '';
        const loginRequired =
          message.includes('Missing Refresh Token') ||
          message.includes('Login required') ||
          (err as any)?.error === 'login_required' ||
          (err as any)?.error === 'consent_required';
        if (loginRequired) {
          // Kick off the redirect AND throw, instead of returning null.
          // Returning null used to send a header-less request, which the
          // server correctly 401'd, which triggered onSessionExpired ->
          // logout() -> a second Auth0 round-trip whose stale appState
          // clobbered the live URL. Throwing here aborts the in-flight
          // request so the 401 cascade never starts.
          redirectToLogin();
          throw new Error('Login required — redirecting to Auth0');
        }
        throw err;
      }
    });

    // When server returns 401, clear local session and redirect to login
    setSessionExpiredHandler(() => {
      resetAnalytics();
      resetCurrentUserCache();
      logout({ logoutParams: { returnTo: window.location.origin } });
    });
  }, [getAccessTokenSilently, loginWithRedirect, logout]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        authorizationParams: { audience: auth0Config.audience, scope: auth0Config.scope },
        // Include search + hash so deep links like /issues?issue=123 survive
        // the Auth0 round-trip. Previously the query string was dropped and
        // the user landed on /issues with no selected issue.
        appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading) return <LoadingSpinner tip="Authenticating..." />;
  // Same spinner during the brief gap between isLoading→false and
  // loginWithRedirect firing — previously this rendered `null` which showed
  // a blank white viewport for a frame or two.
  if (!isAuthenticated) return <LoadingSpinner tip="Redirecting to login..." />;

  return (
    <Router>
      <TriageLayout>
        <RouteSwitch />
      </TriageLayout>
    </Router>
  );
};

/**
 * UX-055: handle unknown routes by redirecting to /triage AND surfacing a
 * toast. The previous `<Navigate>` silently sent the user away, so a typo'd
 * URL from Slack or email vanished without explanation.
 */
const NotFoundRedirect: React.FC = () => {
  const location = useLocation();
  const { message: messageApi } = AntdApp.useApp();
  useEffect(() => {
    messageApi.info(`Unknown route "${location.pathname}" — sent you to Triage.`);
  }, [location.pathname, messageApi]);
  return <Navigate to="/triage" replace />;
};

// Admin-only routes redirect non-admin users to /triage instead of rendering
// the gated component. Server still enforces (every /api/triage-agent/* call
// requires JWT), so this is purely a UX gate to hide the surface during the
// agent-testing phase.
const AdminOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAdmin, loading } = useCurrentUser();
  if (loading) return <LoadingSpinner tip="Checking access…" />;
  if (!isAdmin) return <Navigate to="/triage" replace />;
  return children;
};

const RouteSwitch: React.FC = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/worklist" element={<WorklistPage />} />
      <Route path="/triage" element={<TriageWithContext mode="triage" />} />
      <Route path="/issues" element={<TriageWithContext mode="issues" />} />
      <Route path="/tasks/:locationId?" element={<WorkTasksPage />} />
      <Route path="/actions" element={<ActionsPage />} />
      <Route
        path="/usage"
        element={
          <AdminOnlyRoute>
            <UsagePage />
          </AdminOnlyRoute>
        }
      />
      <Route
        path="/agent-health"
        element={
          <AdminOnlyRoute>
            <TriageAgentHealth />
          </AdminOnlyRoute>
        }
      />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/" element={<Navigate to="/triage" replace />} />
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
};

const App: React.FC = () => {
  if (!isAuth0Configured()) {
    const configError = getAuth0ConfigError();
    return (
      <ConfigProvider theme={antdTheme}>
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          height: '100vh', flexDirection: 'column', gap: 24, padding: '0 24px',
          maxWidth: 600, margin: '0 auto',
        }}>
          <h1 style={{ color: '#ff4d4f' }}>Configuration Required</h1>
          <p style={{ color: '#8c8c8c' }}>{configError}</p>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, width: '100%', overflow: 'auto' }}>
{`VITE_AUTH0_DOMAIN=sensorflow.us.auth0.com
VITE_AUTH0_CLIENT_ID=your-client-id
VITE_AUTH0_AUDIENCE=https://dev-leonid-web/
VITE_API_URL=http://localhost:3002`}
          </pre>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <AntdApp>
        <ErrorBoundary>
          <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            authorizationParams={{
              audience: auth0Config.audience,
              scope: auth0Config.scope,
              redirect_uri: auth0Config.redirectUri,
            }}
            cacheLocation={auth0Config.cacheLocation}
            useRefreshTokens={auth0Config.useRefreshTokens}
            onRedirectCallback={auth0Config.onRedirectCallback}
          >
            <AppWithProviders />
          </Auth0Provider>
        </ErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
